package com.htmlagent.site.state

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.window
import org.w3c.dom.PopStateEvent
import org.w3c.dom.events.Event

/**
 * The layers the back gesture closes on a phone - the page pane, the drawer, the full-screen
 * preview - as steps in the browser history, each marked with its depth.
 *
 * One back pops one step and fires `popstate` for every open layer at once, so each layer closes
 * only when the step it took is gone: when the depth left is below its own. Without the depth, the
 * back that closed the drawer over the page also sent the page back to the chat.
 */
object BackStack {

    private const val KEY = "halDepth"

    private fun depthOf(state: Any?): Int = (state?.asDynamic()?.halDepth as? Number)?.toInt() ?: 0

    /** How many layers are open, read from the current history entry. */
    fun depth(): Int = depthOf(window.history.state)

    /** Takes a step for a layer that has just opened and returns its depth. */
    fun push(): Int {
        val depth = depth() + 1
        val state: dynamic = js("({})")
        state[KEY] = depth
        window.history.pushState(state, "")
        return depth
    }

    /** Closes every open layer in one go, for an action that leaves all of them behind. */
    fun popAll() {
        val depth = depth()
        if (depth > 0) window.history.go(-depth)
    }

    /** The depth left once `popstate` has moved the history. */
    fun depthAfter(event: Event): Int = depthOf((event as? PopStateEvent)?.state)
}

/**
 * Lets the back gesture close something open instead of leaving the app.
 *
 * Opening takes a step in the history and back closes it, so every other way of closing has to
 * spend that step with `history.back()` rather than just flipping the state.
 */
@Composable
fun BackCloses(open: Boolean, onBack: () -> Unit) {
    DisposableEffect(open) {
        if (open) {
            val depth = BackStack.push()
            val listener: (Event) -> Unit = { event -> if (BackStack.depthAfter(event) < depth) onBack() }
            window.addEventListener("popstate", listener)
            onDispose { window.removeEventListener("popstate", listener) }
        } else {
            onDispose { }
        }
    }
}
